/* eslint-disable @typescript-eslint/no-explicit-any */
import * as XLSX from "xlsx-js-style";

interface XLSXOptions {
  filename?: string;
  sheetName?: string;
  minLength?: number; // Minimum column width
}

const FILENAME_DATE = new Date().getTime();
const FILENAME_MODULE = "Transaction_Report";
const FILENAME = `${FILENAME_MODULE}-${FILENAME_DATE}`;

export const ExportToXLSX = <T extends Record<string, unknown>>(
  jsonData: T[],
  options: XLSXOptions = {}
): void => {

  const {
    filename = `${FILENAME}.xlsx`,
    sheetName = "Transactions",
    minLength = 10,
  } = options;

  if (!jsonData?.length) return;

  const headers = Object.keys(jsonData[0]);

  const getNestedValue = (obj: any, path: string): any =>
    path.split(".").reduce((acc, key) => (acc && acc[key] != null ? acc[key] : ""), obj);

  const rows = jsonData.map(row => headers.map(h => getNestedValue(row, h)));

  // Custom top row (above headers)
  const topRow = headers.map((header) => {
    if (header === "Centre") return "Product Groups";
    if (header === "GiftCardSales") return "725";
    if (header === "ServiceFees") return "607";
    if (header === "GreetingCardFees") return "664";
    if (header === "CustomGiftCardFees") return "664";
    if (header === "Courier") return "624";
    if (header === "AdministrationFees") return "704";
    if (header === "BusTickets") return "604";
    if (header === "Carparks") return "605";
    if (header === "KiddyCruisers") return "606";
    if (header === "CarValet") return "729";
    if (header === "ParkingNM") return "734";
    if (header === "HandsFree") return "730";
    if (header === "DeliveryService") return "732";
    if (header === "Miscellanous") return "733";
    if (header === "CarWash") return "731";
    if (header === "Cash") return "Tender Type at Back end";
    return ""; // empty for other columns
  });

  const worksheet = XLSX.utils.aoa_to_sheet([topRow, headers, ...rows]);

  // Style top row (bold)
  topRow.forEach((_, colIndex) => {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: colIndex })];
    if (cell) {
      cell.s = {
        font: { bold: true },
        alignment: { horizontal: "right", vertical: "center" }
      };
    }
  });

  // Style header row (bold + gray + center)
  headers.forEach((_, colIndex) => {
    const cell = worksheet[XLSX.utils.encode_cell({ r: 1, c: colIndex })];
    if (cell) {
      cell.s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "E7E6E6" } },
        alignment: { horizontal: "center", vertical: "center" }
      };
    }
  });

  // Right-align currency columns
  const currencyHeaders = new Set([
    "GiftCardSales",
    "ServiceFees",
    "GreetingCardFees",
    "CustomGiftCardFees",
    "SeasonalGiftCardFees",
    "Courier",
    "AdministrationFees",
    "BusTickets",
    "Carparks",
    "KiddyCruisers",
    "CarValet",
    "ParkingNM",
    "HandsFree",
    "DeliveryService",
    "Miscellanous",
    "CarWash",
    "Total",
    "Cash",
    "Eftpos",
    "Visa",
    "MasterCard",
    "Amex",
    "Diners",
    "GiftCards",
    "DirectCredit",
    "TenderTotal",
  ]);

  headers.forEach((header, colIndex) => {
    if (!currencyHeaders.has(header)) return;

    rows.forEach((_, rowIndex) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex + 2, c: colIndex })];

      if (cell) {
        cell.s = {
          alignment: { horizontal: "right", vertical: "center" }
        };
      }
    });
  });

  // Auto column width with minLength
  worksheet["!cols"] = headers.map((header, colIndex) => {
    const maxLength = Math.max(
      minLength,
      header.length,
      ...rows.map(row => String(row[colIndex] ?? "").length),
      String(topRow[colIndex] ?? "").length
    );
    return { wch: maxLength + 3 };
  });

  // Add Excel filters on header row (row 2)
  worksheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({
      s: { r: 1, c: 0 },
      e: { r: rows.length + 1, c: headers.length - 1 }
    })
  };

  // Freeze header row (row 2)
  worksheet["!freeze"] = { xSplit: 0, ySplit: 2 };

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  XLSX.writeFile(workbook, filename);
};
